import {
  TOTAL_HIBERNATING_KEY,
  TOTAL_NEEDS_FIRMWARE_UPGRADE_KEY,
  TOTAL_NEEDS_SOFTWARE_UPGRADE_KEY,
  TOTAL_OFFLINE_KEY,
  TOTAL_ONLINE_KEY,
} from "constant/device-status";
import { SIDEBAR_ITEMS } from "fleet/constant/navigation-sidebar";
import { Permissions } from "fleet/controller/permissions";
import PropTypes from "prop-types";
import React from "react";
import { Features } from "ui/features";
import { isActivePath } from "util/router";
import { Sidebar, Tree, Item, Badge } from "view/layout/sidebar";

import { Navigation } from "./Navigation";

class NavigationSidebar extends React.PureComponent {
  static get propTypes() {
    return {
      activePath: PropTypes.string.isRequired,
      deviceStatusCount: PropTypes.object,
      featureFlags: PropTypes.object,
      user: PropTypes.object,
    };
  }

  constructor(props) {
    super(props);

    this._renderSidebarItems = this._renderSidebarItems.bind(this);
  }

  _isVeneerNavEnabled() {
    if (!this.props.featureFlags) {
      return false;
    }
    return (
      this.props.featureFlags["is-cortex-veneer-navigation-enabled"] || false
    );
  }

  _renderItemBadges(badges, badgeCounts) {
    if (!badges || !badges.length) {
      return null;
    }

    return badges
      .map((b) => {
        const count = badgeCounts[b.key];
        if (!count && !b.alwaysShow) {
          return null;
        }

        return (
          <Badge
            key={b.key}
            color={b.color}
            title={b.title}
            text={`${count}`}
          />
        );
      })
      .filter((b) => b);
  }

  _renderTreeItems(tree, user, activePath, badgeCounts) {
    const { key, children } = tree;

    return children
      .map((item) => {
        const {
          additionalPaths,
          badges,
          exactMatch,
          feature,
          icon,
          label,
          path,
          rerenderView,
        } = item;

        if (feature && !Features.isEnabled(feature)) {
          return null;
        }

        let isActive = isActivePath(path, activePath, exactMatch);
        if (additionalPaths && additionalPaths.length) {
          isActive =
            isActive ||
            additionalPaths.some((p) => {
              return isActivePath(p.path, activePath, p.exactMatch);
            });
        }

        return (
          <Item
            key={`${key}-${label}`}
            active={isActive}
            badges={this._renderItemBadges(badges, badgeCounts)}
            hide={Permissions.isHidden(user, path)}
            icon={icon}
            label={label}
            link={path}
            rerenderView={rerenderView}
          />
        );
      })
      .filter((i) => i);
  }

  _getBadgeCounts() {
    const { deviceStatusCount } = this.props;
    return {
      "badge-upgrade-software":
        deviceStatusCount[TOTAL_NEEDS_SOFTWARE_UPGRADE_KEY],
      "badge-upgrade-firmware":
        deviceStatusCount[TOTAL_NEEDS_FIRMWARE_UPGRADE_KEY],
      "badge-online": deviceStatusCount[TOTAL_ONLINE_KEY],
      "badge-offline": deviceStatusCount[TOTAL_OFFLINE_KEY],
      "badge-hibernating": deviceStatusCount[TOTAL_HIBERNATING_KEY],
    };
  }

  _renderSidebarItems(items) {
    const { user, activePath } = this.props;
    const badgeCounts = this._getBadgeCounts();

    const sidebarItems = items.map((tree) => {
      const { key, path, icon, label } = tree;
      return (
        <Tree
          key={key}
          active={isActivePath(path, activePath)}
          hide={Permissions.isHidden(user, path)}
          icon={icon}
          label={label}
          link={path}
        >
          {this._renderTreeItems(tree, user, activePath, badgeCounts)}
        </Tree>
      );
    });

    sidebarItems.unshift(
      <Item
        key="overview"
        active={isActivePath("/overview", activePath)}
        hide={Permissions.isHidden(user, "/overview")}
        icon="dashboard"
        label="Overview"
        link="/overview"
      />,
    );

    sidebarItems.push(
      <Item
        key="download"
        active={false}
        icon="download"
        label="Download"
        link="/download/"
        target="_blank"
      />,
    );

    return sidebarItems;
  }

  render() {
    const { user, activePath } = this.props;
    if (this._isVeneerNavEnabled()) {
      return <Navigation activePath={activePath} user={user} />;
    }

    return (
      <Sidebar user={user} activePath={activePath}>
        {this._renderSidebarItems(SIDEBAR_ITEMS)}
      </Sidebar>
    );
  }
}

export { NavigationSidebar };
